import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import Alert from '@mui/material/Alert';
import Avatar from '@mui/material/Avatar';
import InputAdornment from '@mui/material/InputAdornment';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import ToggleButton from '@mui/material/ToggleButton';
import Stack from '@mui/material/Stack';
import HandymanIcon from '@mui/icons-material/Handyman';
import EngineeringIcon from '@mui/icons-material/Engineering';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import api, { errMsg } from '../api/client';
import { useAuth } from '../context/AuthContext';
import { setLanguage } from '../i18n';

const LANGS = [
  { code: 'en', label: 'English' },
  { code: 'hi', label: 'हिंदी' },
  { code: 'te', label: 'తెలుగు' },
];

/** Three steps: phone → otp → profile (only when the account is new) */
const Login = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { login, updateUser } = useAuth();

  const [step, setStep] = useState('phone');
  const [phone, setPhone] = useState('');
  const [otp, setOtp] = useState('');
  const [name, setName] = useState('');
  const [role, setRole] = useState('constructor');
  const [error, setError] = useState('');
  const [info, setInfo] = useState('');
  const [busy, setBusy] = useState(false);

  const sendOtp = async () => {
    setError('');
    setBusy(true);
    try {
      const { data } = await api.post('/api/auth/request-otp', { phone });
      setInfo(data.message);
      setStep('otp');
    } catch (err) {
      setError(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  const verify = async () => {
    setError('');
    setBusy(true);
    try {
      const { data } = await api.post('/api/auth/verify-otp', { phone, otp });
      login(data);
      if (data.user.needsProfile) {
        setStep('profile');
      } else {
        navigate('/');
      }
    } catch (err) {
      setError(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  const completeProfile = async () => {
    setError('');
    if (!name.trim()) {
      setError(t('common.required'));
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post('/api/auth/complete-profile', {
        name,
        role,
        language: i18n.language,
      });
      updateUser(data.user, data.accessToken);
      navigate('/');
    } catch (err) {
      setError(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        px: 2,
      }}
    >
      <Card sx={{ width: '100%', maxWidth: 420 }}>
        <CardContent sx={{ p: 4 }}>
          <Stack alignItems="center" spacing={1} sx={{ mb: 3 }}>
            <Avatar sx={{ bgcolor: 'primary.main', width: 56, height: 56 }}>
              <HandymanIcon sx={{ fontSize: 30, color: 'primary.contrastText' }} />
            </Avatar>
            <Typography variant="h5">{t('appName')}</Typography>
            <Typography variant="body2" color="text.secondary">
              {t('tagline')}
            </Typography>
            <ToggleButtonGroup
              size="small"
              exclusive
              value={i18n.language}
              onChange={(_, v) => v && setLanguage(v)}
            >
              {LANGS.map((l) => (
                <ToggleButton key={l.code} value={l.code} sx={{ px: 1.5 }}>
                  {l.label}
                </ToggleButton>
              ))}
            </ToggleButtonGroup>
          </Stack>

          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          {step === 'phone' && (
            <Stack spacing={2}>
              <TextField
                label={t('login.phone')}
                value={phone}
                onChange={(e) => setPhone(e.target.value.replace(/[^\d]/g, '').slice(0, 10))}
                helperText={t('login.phoneHelp')}
                type="tel"
                autoFocus
                InputProps={{
                  startAdornment: <InputAdornment position="start">+91</InputAdornment>,
                }}
              />
              <Button
                size="large"
                variant="contained"
                disabled={phone.length !== 10 || busy}
                onClick={sendOtp}
              >
                {t('login.sendOtp')}
              </Button>
            </Stack>
          )}

          {step === 'otp' && (
            <Stack spacing={2}>
              {info && <Alert severity="info">{info}</Alert>}
              <Typography variant="body2" color="text.secondary">
                {t('login.otpSentTo', { phone: `+91 ${phone}` })}
              </Typography>
              <TextField
                label={t('login.otp')}
                value={otp}
                onChange={(e) => setOtp(e.target.value.replace(/[^\d]/g, '').slice(0, 6))}
                type="tel"
                autoFocus
                inputProps={{ style: { letterSpacing: 8, textAlign: 'center', fontSize: 22 } }}
              />
              <Button
                size="large"
                variant="contained"
                disabled={otp.length !== 6 || busy}
                onClick={verify}
              >
                {t('login.verify')}
              </Button>
              <Stack direction="row" justifyContent="space-between">
                <Button startIcon={<ArrowBackIcon />} onClick={() => setStep('phone')}>
                  {t('login.changeNumber')}
                </Button>
                <Button onClick={sendOtp} disabled={busy}>
                  {t('login.resend')}
                </Button>
              </Stack>
            </Stack>
          )}

          {step === 'profile' && (
            <Stack spacing={2}>
              <Typography variant="h6">{t('setup.title')}</Typography>
              <TextField
                label={t('setup.name')}
                value={name}
                onChange={(e) => setName(e.target.value)}
                autoFocus
              />
              <Typography variant="subtitle2">{t('setup.iAm')}</Typography>
              <ToggleButtonGroup
                orientation="vertical"
                exclusive
                fullWidth
                value={role}
                onChange={(_, v) => v && setRole(v)}
              >
                <ToggleButton value="constructor" sx={{ justifyContent: 'flex-start', gap: 1.5, px: 2, py: 1.5 }}>
                  <HandymanIcon />
                  <Box sx={{ textAlign: 'left' }}>
                    <Typography variant="subtitle2">{t('setup.constructor')}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      {t('setup.constructorHelp')}
                    </Typography>
                  </Box>
                </ToggleButton>
                <ToggleButton value="worker" sx={{ justifyContent: 'flex-start', gap: 1.5, px: 2, py: 1.5 }}>
                  <EngineeringIcon />
                  <Box sx={{ textAlign: 'left' }}>
                    <Typography variant="subtitle2">{t('setup.worker')}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      {t('setup.workerHelp')}
                    </Typography>
                  </Box>
                </ToggleButton>
              </ToggleButtonGroup>
              <Button size="large" variant="contained" disabled={busy} onClick={completeProfile}>
                {t('setup.continue')}
              </Button>
            </Stack>
          )}
        </CardContent>
      </Card>
    </Box>
  );
};

export default Login;
